import { Localization } from "@viamap/viamap2-common";
import type { LLMAnalysisResultItem } from "./ANNLLMAnalyzer";
import { formatCaseRelevantLabel } from "./ANNServitutFieldAnnotations";
import { getPageFindReason } from "./ANNServitutProvenance";
import { PaataleberettigetResolution } from "./PaataleberettigetResolution";

function formatServitutType(value?: string): string {
   if (!value) return "-";
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") return "Rådighed";
   if (normalized === "tilstandsservitut") return "Tilstand";
   return value;
}

function formatYesNo(value?: boolean): string {
   if (value === true) return Localization.getText("Yes");
   if (value === false) return Localization.getText("No");
   return "-";
}

function formatConfidence(value?: number): string | null {
   if (value === undefined || !Number.isFinite(value)) return null;
   const normalized = value > 1 ? value / 10 : value;
   return `${Math.round(Math.max(0, Math.min(1, normalized)) * 100)}%`;
}

/**
 * Shared AI analysis readout used by the servitut PDF viewer (property info + redegørelse).
 * Phase A shows oversigt, confidence, and why the model kept these pages.
 * Phase B (and other callers) show the full LLM return.
 */
export function ANNAnalysisResultPanel({
   analysisItem,
   pagesKeptCount,
   emptyMeansAll = false,
   mode = "full",
}: {
   analysisItem: LLMAnalysisResultItem;
   pagesKeptCount: number;
   emptyMeansAll?: boolean;
   mode?: "summary" | "full";
}) {
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");
   const confidenceDisplay = formatConfidence(analysisItem.overall_confidence ?? analysisItem.confidence);
   const servitutTypeConfidence = formatConfidence(analysisItem.servitut_type_confidence);
   const paataleConfidence = formatConfidence(analysisItem.paataleberettiget_confidence);
   const caseConfidence = formatConfidence(analysisItem.case_relevance_confidence);
   const summaryOnly = mode === "summary";
   const pageFindReason = getPageFindReason(analysisItem);

   return (
      <div className="ann-pdf-panel">
         <div className="ann-pdf-analysisLeft">
            <strong>{Localization.getText("Summary")}:</strong>
            <p className="ann-pdf-analysisSummaryText">{analysisItem.summary || "-"}</p>
         </div>
         <div className="ann-pdf-analysisMeta">
            <div>
               <strong>{Localization.getText("Confidence")}:</strong> {confidenceDisplay ?? "-"}
            </div>
            <div>
               <strong>{aiSuggestedLabel}:</strong>{" "}
               {pagesKeptCount > 0
                  ? `${pagesKeptCount} ${Localization.getText("pages")}`
                  : emptyMeansAll
                     ? Localization.getText("All pages")
                     : `0 ${Localization.getText("pages")}`}
            </div>
            {summaryOnly || !analysisItem.servitut_type ? null : (
               <div>
                  <strong>Servituttype:</strong> {formatServitutType(analysisItem.servitut_type)}
                  {servitutTypeConfidence ? ` (${servitutTypeConfidence})` : null}
               </div>
            )}
            {summaryOnly || !analysisItem.paataleberettiget ? null : (
               <div>
                  <strong>Historisk påtaleberettiget:</strong> {analysisItem.paataleberettiget}
                  {paataleConfidence ? ` (${paataleConfidence})` : null}
               </div>
            )}
            {summaryOnly || analysisItem.case_relevant === undefined ? null : (
               <div>
                  <strong>{Localization.getText("Problemstilling relevant")}:</strong> {formatCaseRelevantLabel(analysisItem.case_relevant) || "-"}
                  {caseConfidence ? ` (${caseConfidence})` : null}
               </div>
            )}
            {summaryOnly || analysisItem.needs_review === undefined ? null : (
               <div>
                  <strong>{Localization.getText("Needs review")}:</strong> {formatYesNo(analysisItem.needs_review)}
               </div>
            )}
         </div>

         {summaryOnly && pageFindReason ? (
            <div className="ann-pdf-analysisReason">
               <strong>{Localization.getText("Reasoning")}:</strong>
               <p className="ann-pdf-analysisSummaryText">{pageFindReason}</p>
            </div>
         ) : null}

         {summaryOnly || !analysisItem.paataleberettiget_resolution ? null : (
            <PaataleberettigetResolution resolution={analysisItem.paataleberettiget_resolution} />
         )}

         {summaryOnly || !analysisItem.reasoning ? null : (
            <details className="ann-pdf-analysisDetails">
               <summary><strong>{Localization.getText("Reasoning")}</strong></summary>
               <p>{analysisItem.reasoning}</p>
            </details>
         )}

         {summaryOnly || !analysisItem.case_relevance_reasoning ? null : (
            <details className="ann-pdf-analysisDetails">
               <summary><strong>{Localization.getText("Problemstilling")}</strong></summary>
               <p>{analysisItem.case_relevance_reasoning}</p>
            </details>
         )}

         {summaryOnly || !analysisItem.overall_confidence_reasoning ? null : (
            <details className="ann-pdf-analysisDetails">
               <summary><strong>Samlet sikkerhed</strong></summary>
               <p>{analysisItem.overall_confidence_reasoning}</p>
            </details>
         )}

         {summaryOnly || !analysisItem.review_reasons?.length ? null : (
            <details className="ann-pdf-analysisDetails">
               <summary><strong>Review årsager</strong></summary>
               <ul>
                  {analysisItem.review_reasons.map((reason, index) => (
                     <li key={`${reason}-${index}`}>{reason}</li>
                  ))}
               </ul>
            </details>
         )}
      </div>
   );
}
